import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { IconEdit, IconEye, IconTrash } from '@tabler/icons-react'
import { Button, Group, Pill } from '@mantine/core'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { tableDefaultProps, tableDefaultAlertBannerProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { SpeciStatus, SpeciSummary } from '@grower-api-types'
import { SpeciStatusDisplay } from '@shared/components/speci/speci-status-display/speci-status-display'

interface SpeciTableProps {
  data: SpeciSummary[]
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
  deleteSpeci?: (id: string) => void
}

export const SpeciTable = ({
  data = [],
  noDataMessage = 'No specis found for this station',
  isLoading,
  isError,
  deleteSpeci = () => {},
}: SpeciTableProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const navToEditSpeciPage = (id: string) => {
    navigate(`${pathname}/edit/${id}`)
  }

  const navToViewSpeciPage = (id: string) => {
    navigate(`${pathname}/view/${id}`)
  }

  const columns = useMemo<MRT_ColumnDef<SpeciSummary>[]>(
    () => [
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ cell }) => (
          <SpeciStatusDisplay speciStatus={cell.getValue<SpeciStatus | undefined>()} />
        ),
      },
      {
        id: 'author',
        accessorKey: 'author',
        header: 'Author',
      },
      {
        id: 'breeds',
        accessorKey: 'breeds',
        header: 'Breeds',
        Cell: ({ cell }) => (
          <Group gap='xs'>
            {cell
              .getValue<Array<string>>()
              ?.map((value, index) => <Pill key={`breed-${value}-${index}`}>{value}</Pill>)}
          </Group>
        ),
      },
      {
        id: 'totalBales',
        accessorKey: 'totalBales',
        header: 'Total Bales',
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
        },
      },
      {
        id: 'created',
        accessorKey: 'created',
        header: 'Created',
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
      {
        id: 'completed',
        accessorKey: 'completed',
        header: 'Submitted to Zentera',
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
    ],
    []
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Speci table',
    },
    mantineTableBodyRowProps: ({ row }) => ({
      'data-testid': `speci-row-${row.original.id}`,
      ref: undefined, // Ensure compatibility with the expected type
    }),
    enableSorting: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) =>
      row.original.status === SpeciStatus.InProgress ? (
        <Group>
          <Button
            size='xs'
            variant='outline'
            onClick={() => navToEditSpeciPage(row.original.id)}
            leftSection={<IconEdit />}
          >
            Continue
          </Button>
          <Button
            size='xs'
            color='red'
            variant='outline'
            onClick={() => deleteSpeci(row.original.id)}
            leftSection={<IconTrash />}
          >
            Delete
          </Button>
        </Group>
      ) : (
        <Group>
          <Button
            size='xs'
            variant='outline'
            onClick={() => navToViewSpeciPage(row.original.id)}
            leftSection={<IconEye />}
          >
            View
          </Button>
        </Group>
      ),
  })

  return <MantineReactTable table={table} />
}
